import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, homePath } from "@/lib/auth.jsx";
import { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

export default function VerifyEmail() {
  const { verifyEmail } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState("loading"); // loading | success | error
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        setState("error");
        setErr("Missing verification token. Open the link from your email.");
        return;
      }
      try {
        const user = await verifyEmail(token);
        if (cancelled) return;
        setState("success");
        setTimeout(() => {
          navigate(homePath(user), { replace: true });
        }, 1200);
      } catch (e) {
        if (cancelled) return;
        setState("error");
        setErr(formatApiError(e.response?.data?.detail) || e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="px-6 sm:px-12 py-6 border-b border-neutral-100">
        <Logo />
      </header>
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center" data-testid="verify-email-card">
          {state === "loading" && (
            <>
              <div className="w-16 h-16 mx-auto rounded-2xl bg-propera-light text-propera flex items-center justify-center mb-6">
                <Loader2 size={26} className="animate-spin" />
              </div>
              <h1 className="text-3xl font-bold text-neutral-900">Verifying your email…</h1>
              <p className="text-neutral-500 mt-2">Hang tight — this only takes a second.</p>
            </>
          )}
          {state === "success" && (
            <>
              <div className="w-16 h-16 mx-auto rounded-2xl bg-propera-light text-propera flex items-center justify-center mb-6">
                <CheckCircle2 size={26} />
              </div>
              <h1 className="text-3xl font-bold text-neutral-900" data-testid="verify-success-heading">You&apos;re verified ✓</h1>
              <p className="text-neutral-500 mt-2">Redirecting you to your dashboard…</p>
            </>
          )}
          {state === "error" && (
            <>
              <div className="w-16 h-16 mx-auto rounded-2xl bg-red-50 text-red-600 flex items-center justify-center mb-6">
                <AlertCircle size={26} />
              </div>
              <h1 className="text-3xl font-bold text-neutral-900">Verification failed</h1>
              <p className="text-neutral-500 mt-2" data-testid="verify-error-msg">{err || "This link is invalid or has expired."}</p>
              <div className="mt-8 space-y-2">
                <Link to="/login" data-testid="verify-back-login" className="inline-block bg-propera hover:bg-propera-hover text-white rounded-full px-6 py-3 font-semibold transition">
                  Back to Sign In
                </Link>
                <p className="text-xs text-neutral-500">Need a fresh link? Sign in with your email and tap &quot;Resend verification&quot;.</p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
